import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import {
  Home,
  FileText,
  CreditCard,
  Wrench,
  ChevronRight,
  ChevronLeft,
  X,
  CheckCircle2,
  MapPin,
  Calendar,
  DollarSign,
  ArrowRight,
} from 'lucide-react';

const STEPS = [
  { key: 'unit', icon: Home, title: 'Your Unit' },
  { key: 'lease', icon: FileText, title: 'Your Lease' },
  { key: 'payments', icon: CreditCard, title: 'Paying Rent' },
  { key: 'maintenance', icon: Wrench, title: 'Maintenance' },
];

const TenantOnboardingWizard = ({ unit, lease, property, onComplete, onDismiss }) => {
  const [step, setStep] = useState(0);

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else onComplete();
  };
  const prev = () => { if (step > 0) setStep(step - 1); };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6" data-testid="onboarding-step-unit">
            <div className="w-16 h-16 bg-gold/10 flex items-center justify-center mx-auto">
              <Home className="w-8 h-8 text-gold" />
            </div>
            <div className="text-center">
              <h3 className="font-serif text-2xl mb-2">
                Welcome to{' '}
                <span className="text-gold">{unit?.unit_number || 'your new home'}</span>
              </h3>
              <p className="text-muted-foreground">Here's everything about your unit</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] p-5 space-y-3">
              {property && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Property</p>
                    <p className="font-medium">{property.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {property.address}{property.city ? `, ${property.city}` : ''}{property.state ? `, ${property.state}` : ''} {property.zip_code || ''}
                    </p>
                  </div>
                </div>
              )}
              {unit && (
                <>
                  <div className="flex items-start gap-3">
                    <Home className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground">Unit Details</p>
                      <p className="font-medium">
                        {unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms} Bed`} / {unit.bathrooms} Bath
                        {unit.square_feet ? ` / ${unit.square_feet} sqft` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly Rent</p>
                      <p className="font-medium text-gold text-lg">${unit.rent_amount?.toLocaleString()}/mo</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6" data-testid="onboarding-step-lease">
            <div className="w-16 h-16 bg-blue-500/10 flex items-center justify-center mx-auto">
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
            <div className="text-center">
              <h3 className="font-serif text-2xl mb-2">Your Lease</h3>
              <p className="text-muted-foreground">Key dates and terms to know</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] p-5 space-y-4">
              {lease ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm text-muted-foreground">Start Date</p>
                        <p className="font-medium">{lease.start_date}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm text-muted-foreground">End Date</p>
                        <p className="font-medium">{lease.end_date}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly Rent</p>
                      <p className="font-medium text-lg">${lease.monthly_rent?.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        {lease.status}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground pt-2 border-t border-[#e5e7eb]">
                    You can view your full lease and download any attached documents from the <strong className="text-foreground">My Lease</strong> page in the sidebar.
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground text-center py-4">No active lease found yet. Contact your landlord.</p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6" data-testid="onboarding-step-payments">
            <div className="w-16 h-16 bg-gold/10 flex items-center justify-center mx-auto">
              <CreditCard className="w-8 h-8 text-gold" />
            </div>
            <div className="text-center">
              <h3 className="font-serif text-2xl mb-2">Paying Rent</h3>
              <p className="text-muted-foreground">Simple, secure online payments</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] p-5 space-y-4">
              <div className="space-y-3">
                {[
                  { num: '1', text: 'Go to the Payments page from the sidebar' },
                  { num: '2', text: 'Find your pending payment and click "Pay Now"' },
                  { num: '3', text: 'Complete checkout securely via Stripe' },
                  { num: '4', text: 'You\'ll get an email confirmation once paid' },
                ].map((item) => (
                  <div key={item.num} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-gold/20 text-gold text-xs font-bold flex items-center justify-center shrink-0">
                      {item.num}
                    </span>
                    <p className="text-sm">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-[#e5e7eb] space-y-2">
                <p className="text-xs text-amber-400 flex items-start gap-2">
                  <span className="shrink-0">!</span>
                  Rent is due on the 1st of each month. A reminder is sent on the 3rd if unpaid.
                </p>
                <p className="text-xs text-red-400 flex items-start gap-2">
                  <span className="shrink-0">!</span>
                  A 5% late fee is automatically applied after the 5th if payment is not received.
                </p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6" data-testid="onboarding-step-maintenance">
            <div className="w-16 h-16 bg-purple-500/10 flex items-center justify-center mx-auto">
              <Wrench className="w-8 h-8 text-purple-400" />
            </div>
            <div className="text-center">
              <h3 className="font-serif text-2xl mb-2">Maintenance Requests</h3>
              <p className="text-muted-foreground">Something broken? We've got you covered</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] p-5 space-y-4">
              <div className="space-y-3">
                {[
                  { num: '1', text: 'Navigate to the Maintenance page from the sidebar' },
                  { num: '2', text: 'Click "New Request" and describe the issue' },
                  { num: '3', text: 'Set a priority level (Low, Medium, High, Emergency)' },
                  { num: '4', text: 'Track the status as your landlord works on it' },
                ].map((item) => (
                  <div key={item.num} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-purple-500/20 text-purple-400 text-xs font-bold flex items-center justify-center shrink-0">
                      {item.num}
                    </span>
                    <p className="text-sm">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground pt-3 border-t border-[#e5e7eb]">
                For emergencies (fire, flood, gas leak), call 911 first, then submit a request with <strong className="text-foreground">Emergency</strong> priority.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="onboarding-wizard">
      <div className="relative w-full max-w-lg mx-4 bg-card border border-border shadow-2xl animate-fadeIn overflow-hidden">
        {/* Close / Skip */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10"
          data-testid="onboarding-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress bar */}
        <div className="flex gap-1 px-6 pt-6">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={`h-1 flex-1 transition-colors duration-300 ${
                i <= step ? 'bg-gold' : 'bg-[#f7f8fa]'
              }`}
            />
          ))}
        </div>

        {/* Step labels */}
        <div className="flex justify-between px-6 pt-2 pb-4">
          {STEPS.map((s, i) => (
            <span key={s.key} className={`text-[10px] uppercase tracking-wider ${i <= step ? 'text-gold' : 'text-muted-foreground/50'}`}>
              {s.title}
            </span>
          ))}
        </div>

        {/* Content */}
        <div className="px-6 pb-4 min-h-[380px]">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between">
          <div>
            {step > 0 ? (
              <Button variant="ghost" onClick={prev} className="text-muted-foreground hover:text-foreground" data-testid="onboarding-prev-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : (
              <button
                onClick={onDismiss}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="onboarding-skip-btn"
              >
                Skip tour
              </button>
            )}
          </div>
          <Button onClick={next} className="bg-gold text-white hover:bg-gold-light" data-testid="onboarding-next-btn">
            {step < STEPS.length - 1 ? (
              <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
            ) : (
              <>Get Started <ArrowRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>

        {/* Don't show again */}
        <div className="border-t border-[#e5e7eb] px-6 py-3 flex items-center justify-center">
          <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="onboarding-dont-show-label">
            <input
              type="checkbox"
              className="accent-gold w-3.5 h-3.5"
              onChange={(e) => {
                if (e.target.checked) {
                  onComplete();
                }
              }}
              data-testid="onboarding-dont-show-checkbox"
            />
            Don't show this again
          </label>
        </div>
      </div>
    </div>
  );
};

export default TenantOnboardingWizard;
